import Pusher, { Channel } from "pusher-js";

type EventCallback<T> = (data: T) => void;
type BoundCallback = (...args: unknown[]) => void;

const key = import.meta.env.VITE_PUSHER_KEY;
const cluster = import.meta.env.VITE_PUSHER_CLUSTER;

const pusher = new Pusher(key, { cluster });

// Three-level ref-counting:
//   channelName -> eventName -> Set<callback>
//
// We need per-callback granularity (not per-channel like chatPusher.service)
// because several hooks share the same (channel, event) tuple with different
// callbacks (e.g. useJobUpdates + useJobProgress on `jobs_channel_${orgId}`).
// Tearing the channel down when any one of them unmounts would silently
// break the others.
const channels = new Map<string, Channel>();
const subscriptions = new Map<string, Map<string, Set<BoundCallback>>>();

const getOrCreateEventMap = (channelName: string): Map<string, Set<BoundCallback>> => {
  let eventMap = subscriptions.get(channelName);
  if (!eventMap) {
    eventMap = new Map();
    subscriptions.set(channelName, eventMap);
  }
  return eventMap;
};

const getOrCreateCallbackSet = (
  eventMap: Map<string, Set<BoundCallback>>,
  eventName: string,
): Set<BoundCallback> => {
  let callbackSet = eventMap.get(eventName);
  if (!callbackSet) {
    callbackSet = new Set();
    eventMap.set(eventName, callbackSet);
  }
  return callbackSet;
};

export const subscribe = <T>(channelName: string, eventName: string, callback: EventCallback<T>) => {
  const boundCallback = callback as unknown as BoundCallback;

  const eventMap = getOrCreateEventMap(channelName);
  const callbackSet = getOrCreateCallbackSet(eventMap, eventName);

  // First subscriber on this channel? Open the Pusher channel.
  let channel = channels.get(channelName);
  if (!channel) {
    channel = pusher.subscribe(channelName);
    channels.set(channelName, channel);
  }

  // First callback on this event for this channel? Bind once.
  // Subsequent callbacks just join the Set — Pusher would happily invoke a
  // single bound handler for them all, but we keep per-callback bindings so
  // unbinding a single subscriber stays surgical.
  channel.bind(eventName, boundCallback);
  callbackSet.add(boundCallback);
};

export const unsubscribe = <T>(channelName: string, eventName: string, callback: EventCallback<T>) => {
  const eventMap = subscriptions.get(channelName);
  if (!eventMap) return;

  const callbackSet = eventMap.get(eventName);
  if (!callbackSet) return;

  const boundCallback = callback as unknown as BoundCallback;
  if (!callbackSet.has(boundCallback)) return;

  const channel = channels.get(channelName);
  if (channel) {
    channel.unbind(eventName, boundCallback);
  }
  callbackSet.delete(boundCallback);

  // Last callback on this event? Drop the event tracking entry.
  if (callbackSet.size === 0) {
    eventMap.delete(eventName);
  }

  // Last event on this channel? Tear down the channel itself so we stop
  // receiving traffic we no longer care about.
  if (eventMap.size === 0) {
    subscriptions.delete(channelName);
    if (channel) {
      pusher.unsubscribe(channelName);
      channels.delete(channelName);
    }
  }
};

export const disconnect = () => {
  pusher.disconnect();
  channels.clear();
  subscriptions.clear();
};

export const reconnectPusher = () => {
  if (pusher.connection.state === "connected") {
    pusher.disconnect();
  }
  pusher.connect();
};

export const getPusherState = () => {
  return pusher.connection.state;
};

// Test-only: lets the unit suite assert that internal bookkeeping is empty
// after sequences of subscribe/unsubscribe. Not exported from any barrel.
export const __getInternalStateForTests = () => ({
  channels,
  subscriptions,
});
